import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation, Link } from "react-router-dom";
import {
  LayoutDashboard, FileText, Quote, Briefcase, HelpCircle, Users,
  Inbox, CalendarCheck, Settings as SettingsIcon, LogOut, Menu, X, ExternalLink,
  UserCog, ScrollText, Contact, FolderOpen, ListChecks, CalendarDays, FileSignature,
  PoundSterling, ShieldCheck, MessageSquareWarning, BarChart3, PenTool, Bell, Clock,
  Database, Lock as LockIcon, ChevronDown, Globe,
} from "lucide-react";
import { ApexLogo } from "@/components/ApexLogo";
import { useAuth } from "@/context/AuthContext";

const DASHBOARD = { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true };

// Grouped navigation. Every existing link is preserved (Templates kept under
// "Documents & Follow-up"). Groups are collapsible on all screen sizes.
const GROUPS = [
  {
    label: "Website Content", icon: Globe,
    items: [
      { to: "/admin/articles", label: "Articles", icon: FileText, module: "cms", action: "view" },
      { to: "/admin/testimonials", label: "Testimonials", icon: Quote, module: "cms", action: "view" },
      { to: "/admin/services", label: "Services", icon: Briefcase, module: "cms", action: "view" },
      { to: "/admin/faqs", label: "FAQs", icon: HelpCircle, module: "cms", action: "view" },
      { to: "/admin/team", label: "Team", icon: Users, module: "cms", action: "view" },
    ],
  },
  {
    label: "Clients & Casework", icon: FolderOpen,
    items: [
      { to: "/admin/enquiries", label: "Enquiries", icon: Inbox, module: "enquiries", action: "view" },
      { to: "/admin/consultations", label: "Consultations", icon: CalendarCheck, module: "consultations", action: "view" },
      { to: "/admin/clients", label: "Clients", icon: Contact, module: "clients", action: "view" },
      { to: "/admin/matters", label: "Matters", icon: FolderOpen, module: "matters", action: "view" },
      { to: "/admin/tasks", label: "Tasks", icon: ListChecks, module: "tasks", action: "view" },
      { to: "/admin/calendar", label: "Calendar", icon: CalendarDays, module: "calendar", action: "view" },
    ],
  },
  {
    label: "Documents & Follow-up", icon: FileSignature,
    items: [
      { to: "/admin/templates", label: "Templates", icon: FileSignature, module: "templates", action: "view" },
      { to: "/admin/signatures", label: "Signatures", icon: PenTool, module: "signatures", action: "view" },
      { to: "/admin/reminders", label: "Reminders", icon: Bell, module: "reminders", action: "view" },
    ],
  },
  {
    label: "Business & Finance", icon: PoundSterling,
    items: [
      { to: "/admin/time", label: "Time Tracking", icon: Clock, module: "time", action: "view" },
      { to: "/admin/finance", label: "Finance", icon: PoundSterling, module: "finance", action: "view" },
      { to: "/admin/reports", label: "Reports", icon: BarChart3, module: "reports", action: "view" },
    ],
  },
  {
    label: "Compliance", icon: ShieldCheck,
    items: [
      { to: "/admin/compliance", label: "Compliance", icon: ShieldCheck, module: "compliance", action: "view" },
      { to: "/admin/policies", label: "Policies", icon: ScrollText, module: "compliance", action: "view" },
      { to: "/admin/complaints", label: "Complaints", icon: MessageSquareWarning, module: "complaints", action: "view" },
      { to: "/admin/audit", label: "Audit Trail", icon: ScrollText, module: "audit", action: "view" },
    ],
  },
  {
    label: "Administration", icon: UserCog,
    items: [
      { to: "/admin/users", label: "Users & Roles", icon: UserCog, flag: "user_administration" },
      { to: "/admin/data", label: "Import / Export", icon: Database, module: "dataio", action: "view" },
      { to: "/admin/security", label: "Security", icon: LockIcon },
      { to: "/admin/settings", label: "Settings", icon: SettingsIcon, module: "settings", action: "view" },
    ],
  },
];

const slug = (s) => s.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "");

export default function AdminLayout() {
  const { user, logout, can, hasFlag } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);

  const canSee = (l) => (l.flag ? hasFlag(l.flag) : l.module ? can(l.module, l.action) : true);

  // groups with their visible items, dropping empty groups
  const visibleGroups = GROUPS
    .map((g) => ({ ...g, visibleItems: g.items.filter(canSee) }))
    .filter((g) => g.visibleItems.length > 0);

  const activeGroup = visibleGroups.find((g) =>
    g.visibleItems.some((i) => pathname === i.to || pathname.startsWith(i.to + "/"))
  )?.label;

  const [expanded, setExpanded] = useState({});
  useEffect(() => {
    if (activeGroup) setExpanded((e) => ({ ...e, [activeGroup]: true }));
  }, [activeGroup]);

  const doLogout = async () => { await logout(); navigate("/admin/login"); };
  const toggle = (label) => setExpanded((e) => ({ ...e, [label]: !e[label] }));
  const closeMobile = () => setOpen(false);

  const itemClass = ({ isActive }) =>
    `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
      isActive ? "bg-gold text-navy" : "text-slate-300 hover:bg-navy-light"
    }`;

  const Sidebar = (
    <aside className="flex h-full w-64 flex-col bg-[#08121E]">
      <div className="flex items-center justify-between border-b border-navy-light px-5 py-5">
        <Link to="/admin" onClick={closeMobile}><ApexLogo light /></Link>
        <button className="lg:hidden text-slate-400" onClick={closeMobile}><X size={20} /></button>
      </div>
      <nav className="flex-1 space-y-1 overflow-y-auto p-3">
        <NavLink to={DASHBOARD.to} end={DASHBOARD.end} onClick={closeMobile}
          data-testid="admin-nav-dashboard" className={itemClass}>
          <DASHBOARD.icon size={18} /> {DASHBOARD.label}
        </NavLink>

        {visibleGroups.map((g) => {
          const isOpen = !!expanded[g.label];
          return (
            <div key={g.label} className="pt-1.5">
              <button
                type="button"
                onClick={() => toggle(g.label)}
                data-testid={`admin-navgroup-${slug(g.label)}`}
                aria-expanded={isOpen}
                className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-400 hover:bg-navy-light hover:text-slate-200"
              >
                <g.icon size={15} className="text-gold/70" />
                <span className="flex-1">{g.label}</span>
                <ChevronDown size={15} className={`transition-transform ${isOpen ? "" : "-rotate-90"}`} />
              </button>
              {isOpen && (
                <div className="mt-1 space-y-1 pl-2">
                  {g.visibleItems.map((l) => (
                    <NavLink key={l.to} to={l.to} onClick={closeMobile}
                      data-testid={`admin-nav-${slug(l.label)}`} className={itemClass}>
                      <l.icon size={18} /> {l.label}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </nav>
      <div className="border-t border-navy-light p-3">
        <a href="/" target="_blank" rel="noreferrer" className="mb-1 flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-slate-400 hover:bg-navy-light">
          <ExternalLink size={16} /> View website
        </a>
        <button onClick={doLogout} data-testid="admin-logout-btn" className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm text-slate-300 hover:bg-navy-light">
          <LogOut size={16} /> Sign out
        </button>
      </div>
    </aside>
  );

  return (
    <div className="flex min-h-screen bg-slate-100">
      <div className="hidden lg:block">{Sidebar}</div>
      {open && (
        <div className="fixed inset-0 z-50 flex lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={closeMobile} />
          <div className="relative">{Sidebar}</div>
        </div>
      )}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex items-center justify-between border-b border-slate-200 bg-white px-5 py-3.5">
          <button className="lg:hidden text-navy" onClick={() => setOpen(true)} data-testid="admin-mobile-menu-btn"><Menu size={22} /></button>
          <div className="hidden text-sm text-slate-500 lg:block">ApexPath Administration</div>
          <div className="flex items-center gap-3 text-sm">
            <span className="hidden text-slate-500 sm:block">{user?.email}</span>
            <span className="rounded-full bg-navy px-3 py-1 text-xs font-semibold uppercase text-gold">{user?.role}</span>
          </div>
        </header>
        <main className="min-w-0 flex-1 overflow-y-auto p-5 sm:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
